import { Box, Button, Text } from "@mantine/core"
import { CheckIcon, FileTextIcon, ReceiptIcon, TruckIcon } from "@phosphor-icons/react"
import { ReactNode, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { HelpIcon, useHelpContext } from "@dehub/theme"
import { useNavigate } from "react-router"
import useSWR from "swr"
import { InvoiceApi, OrderApi, ReceivedQuotationApi } from "../../../apis"
import { DotPill } from "../../../components/delivery/atoms"
import { usePermission } from "../../../components/permission"
import { PermissionButton } from "../../../components/ui"
import { currencyFormat } from "../../../utils"
import { dateFormat } from "../../../utils/date"
import classes from "./buyer-actions.module.css"

/** The buyer's own order query. `type` empty = the caller's purchases (as buyer-sales-list does). */
const buyerOrders = (orderStatus: string, deliveryNoteStatus = "") =>
  OrderApi.list({
    filter: { type: "", orderStatus, deliveryNoteStatus, excel: "false" },
    offset: { page: 1, limit: 1 },
  })

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const orderCode = (o: any) => o?.purchaseCode || o?.salesCode || "-"

interface CardProps {
  icon: ReactNode
  title: string
  count: number | null
  text: string
  pill?: ReactNode
  action: ReactNode
}

function AnswerCard({ icon, title, count, text, pill, action }: CardProps) {
  return (
    <Box className={classes.card}>
      <Box className={classes.cardHead}>
        <Box className={classes.titleWrap}>
          <Box className={classes.iconBox}>{icon}</Box>
          <Text className={classes.cardTitle}>{title}</Text>
        </Box>
        <Text className={classes.count}>{count ?? "-"}</Text>
      </Box>
      <Text className={classes.text}>{text}</Text>
      <Box className={classes.actions}>{pill}</Box>
      <Box className={classes.actions}>{action}</Box>
    </Box>
  )
}

/**
 * Buyer home (canvas OrdB_Home) «Таны хариу хүлээж буй»: four cards. Existing endpoints only:
 * POST /ord/order/list (REVIEWED = waits for the buyer's approval; deliveryNoteStatus DELIVERING = on the way)
 * the received-quotation list and GET /inv/invoice/received (CONFIRMED = still to pay; its summary
 * carries the overdue count), which closes finding B53.
 */
export function BuyerActionsRow() {
  const { t } = useTranslation(["dashboard", "quotation"])
  const navigate = useNavigate()
  const QUOTE_LIST = usePermission("PRICING_QUOTATION_LIST")
  const INV_LIST = usePermission("INV_LIST")

  const pending = useSWR("dash.buyer.pending-orders", () => buyerOrders("REVIEWED"), { revalidateOnFocus: false })
  const delivering = useSWR("dash.buyer.delivering-orders", () => buyerOrders("", "DELIVERING"), {
    revalidateOnFocus: false,
  })
  const quotes = useSWR(
    QUOTE_LIST.hasAccess ? "dash.buyer.sent-quotations" : null,
    () => ReceivedQuotationApi.list({ offset: { page: 1, limit: 1 }, filter: { status: "SENT" } }),
    { revalidateOnFocus: false },
  )

  const invoices = useSWR(
    INV_LIST.hasAccess ? "dash.buyer.invoices-to-pay" : null,
    () => InvoiceApi.received({ filter: { status: "CONFIRMED" }, offset: { page: 1, limit: 1 } }),
    { revalidateOnFocus: false },
  )

  const invoiceCount: number | null = !INV_LIST.hasAccess || invoices.error ? null : (invoices.data?.count ?? null)
  const overdueCount = invoices.data?.summary?.overdueCount ?? 0
  const lastInvoice = invoices.data?.rows?.[0]
  const pendingCount: number | null = pending.error ? null : (pending.data?.count ?? null)
  const deliveringCount: number | null = delivering.error ? null : (delivering.data?.count ?? null)
  const quoteCount: number | null = !QUOTE_LIST.hasAccess || quotes.error ? null : (quotes.data?.count ?? null)
  // @dehub/help: the four counts pick the dashboard's «Одоо» card for the buyer.
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    setHelpContext({
      vars: { approveCount: pendingCount ?? 0, invoiceCount: invoiceCount ?? 0, quoteCount: quoteCount ?? 0, receiveCount: deliveringCount ?? 0 },
    })
  }, [pendingCount, invoiceCount, quoteCount, deliveringCount, setHelpContext])
  const firstPending = pending.data?.rows?.[0]
  const firstDelivering = delivering.data?.rows?.[0]
  const firstQuote = quotes.data?.rows?.[0]

  return (
    <Box>
      <h2 className={classes.sectionTitle}>
        {t("dashboard:buyerActions.title")}
        <HelpIcon helpKey="order.dashboard.section.buyer-actions" variant="section" />
      </h2>
      <Box className={classes.grid4}>
        <AnswerCard
          icon={<ReceiptIcon size={18} />}
          title={t("dashboard:buyerActions.invoices")}
          count={invoiceCount}
          text={
            lastInvoice
              ? [
                  t("dashboard:buyerActions.invoicesLast", {
                    code: lastInvoice.refCode,
                    amount: currencyFormat(Number(lastInvoice.amountToPay) || 0),
                    order: lastInvoice.orderPurchaseCode || lastInvoice.respondText || "-",
                  }),
                  overdueCount > 0 ? t("dashboard:buyerActions.invoicesOverdue", { count: overdueCount }) : "",
                ].filter(Boolean).join(" ")
              : invoiceCount === 0
                ? t("dashboard:buyerActions.invoicesNone")
                : t("dashboard:buyerActions.invoicesHint")
          }
          pill={overdueCount > 0 ? <DotPill tone="amber">{t("dashboard:buyerActions.overduePill", { count: overdueCount })}</DotPill> : null}
          action={
            <Button variant="default" onClick={() => navigate("/buyer-sales")}>
              {t("dashboard:buyerActions.confirmPayment")}
            </Button>
          }
        />

        <AnswerCard
          icon={<FileTextIcon size={18} />}
          title={t("dashboard:buyerActions.quotes")}
          count={quoteCount}
          text={
            firstQuote
              ? t("dashboard:buyerActions.quotesHint", {
                  code: firstQuote.refCode,
                  amount: currencyFormat(firstQuote.totalAmount || 0),
                  until: dateFormat(firstQuote.validUntil),
                })
              : t("dashboard:buyerActions.quotesNone")
          }
          pill={quoteCount ? <DotPill tone="green">{t("quotation:status.SENT")}</DotPill> : null}
          action={
            <PermissionButton permission="PRICING_QUOTATION_LIST" variant="default" onClick={() => navigate("/received-quotation")}>
              {t("dashboard:buyerActions.viewQuotes")}
            </PermissionButton>
          }
        />

        <AnswerCard
          icon={<CheckIcon size={18} />}
          title={t("dashboard:buyerActions.awaitingApproval")}
          count={pendingCount}
          text={
            firstPending
              ? t("dashboard:buyerActions.pendingOrdersHint", {
                  code: orderCode(firstPending),
                  amount: currencyFormat(firstPending.totalAmount || 0),
                })
              : t("dashboard:buyerActions.pendingOrdersNone")
          }
          pill={pendingCount ? <DotPill tone="amber">{t("dashboard:buyerActions.needsAnswer")}</DotPill> : null}
          action={
            <Button variant="default" onClick={() => navigate("/buyer-sales")}>
              {t("dashboard:buyerActions.viewList")}
            </Button>
          }
        />

        <AnswerCard
          icon={<TruckIcon size={18} />}
          title={t("dashboard:buyerActions.receive")}
          count={deliveringCount}
          text={
            firstDelivering
              ? t("dashboard:buyerActions.receiveHint", { code: orderCode(firstDelivering) })
              : t("dashboard:buyerActions.receiveNone")
          }
          pill={deliveringCount ? <DotPill tone="grey">{t("dashboard:buyerActions.inTransit")}</DotPill> : null}
          action={
            <Button variant="default" onClick={() => navigate("/buyer-sales")}>
              {t("dashboard:buyerActions.viewDeliveries")}
            </Button>
          }
        />
      </Box>
    </Box>
  )
}
